import React from 'react';
import { ViewMode } from '../types';
import { LayoutDashboard, Lightbulb, Archive, MessageSquareText, LogOut, RefreshCw } from 'lucide-react';

interface LayoutProps {
  currentView: ViewMode;
  onChangeView: (view: ViewMode) => void;
  onLogout: () => void;
  onRefresh: () => void;
  isRefreshing: boolean;
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ 
  currentView, onChangeView, onLogout, onRefresh, isRefreshing, children 
}) => {
  // Reordered according to user request: My Ideas -> AI Chat -> Reports -> Archive
  const navItems = [
    { id: ViewMode.IDEAS, label: 'My Ideas', icon: <Lightbulb size={20} /> },
    { id: ViewMode.CHAT, label: 'AI Chat', icon: <MessageSquareText size={20} /> },
    { id: ViewMode.DASHBOARD, label: 'Reports', icon: <LayoutDashboard size={20} /> },
    { id: ViewMode.ARCHIVE, label: 'Archive', icon: <Archive size={20} /> },
  ];

  const mainOverflowClass = currentView === ViewMode.CHAT ? 'overflow-hidden' : 'overflow-auto';

  return (
    // Use h-[100dvh] for dynamic viewport height to fix mobile/iframe cutoff issues
    <div className="flex h-screen supports-[height:100dvh]:h-[100dvh] bg-gray-50 text-gray-800 font-sans overflow-hidden">
      {/* Sidebar - Desktop */}
      <aside className="hidden md:flex flex-col w-64 bg-white border-r border-gray-200 shadow-sm z-10">
        <div className="p-6 border-b border-gray-100 flex items-center justify-center">
             <div className="bg-red-50 p-2 rounded-full">
                <Lightbulb className="text-primary" size={24} />
             </div>
             <span className="ml-2 font-bold text-xl text-secondary tracking-tight">Idea Notes</span>
        </div>
        
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => onChangeView(item.id)}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                currentView === item.id 
                  ? 'bg-red-50 text-primary font-medium shadow-sm' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-100">
          <button 
            onClick={onLogout}
            className="w-full flex items-center space-x-3 px-4 py-2 text-gray-500 hover:bg-gray-50 hover:text-primary rounded-lg transition-colors"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile Bottom Nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 h-16 bg-white border-t z-50 shadow-[0_-1px_3px_rgba(0,0,0,0.05)]">
         <div className="grid grid-cols-5 h-full">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => onChangeView(item.id)}
                className={`flex flex-col items-center justify-center space-y-1 w-full h-full active:bg-gray-50 ${
                  currentView === item.id ? 'text-primary' : 'text-gray-400'
                }`}
              >
                {item.icon}
                <span className="text-[10px] font-medium truncate w-full text-center px-1">{item.label}</span>
              </button>
            ))}
            <button 
              onClick={onLogout}
              className="flex flex-col items-center justify-center space-y-1 text-gray-400 active:bg-gray-50"
            >
              <LogOut size={20} />
              <span className="text-[10px] font-medium">Exit</span>
            </button>
         </div>
      </div>

      {/* Main Content */}
      <main className={`flex-1 ${mainOverflowClass} md:p-8 p-4 pb-20 md:pb-8 w-full max-w-7xl mx-auto flex flex-col relative`}>
        {/* Added min-h-0 to ensure flex child can scroll internally */}
        <div className="flex-1 min-h-0 relative flex flex-col">
            {children}
        </div>
      </main>
    </div>
  );
};